import { useState } from "react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const LOOKING_FOR = [
    "Incubator",
    "Accelerator",
    "Grant",
    "Investor",
    "Mentor",
    "Strategic partner",
    "Founder introduction",
    "Other",
];

const STAGES = [
    "Idea / pre-formation",
    "Early stage / MVP",
    "Angel / pre-seed",
    "Seed",
    "Series A / growth",
    "Later stage",
];

export default function Apply() {
    const [form, setForm] = useState({
        name: "", email: "", linkedin: "", phone: "",
        company: "", website: "", sector: "", geography: "", stage: "",
        looking_for: [], goal: "", additional: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const toggleLooking = (v) => {
        setForm((f) => ({
            ...f,
            looking_for: f.looking_for.includes(v)
                ? f.looking_for.filter((x) => x !== v)
                : [...f.looking_for, v],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const message = [
                `LinkedIn: ${form.linkedin || "—"}`,
                `Phone: ${form.phone || "—"}`,
                `Company: ${form.company || "—"} · ${form.website || ""}`,
                `Sector: ${form.sector || "—"} · Geo: ${form.geography || "—"} · Stage: ${form.stage || "—"}`,
                `Looking for: ${form.looking_for.join(", ") || "—"}`,
                "",
                "Goal:",
                form.goal || "—",
                "",
                "Additional:",
                form.additional || "—",
            ].join("\n");
            await api.post("/contact", {
                name: form.name,
                email: form.email,
                topic: "Ecosystem Access Application",
                message,
            });
            toast.success("Application received. We'll be in touch.");
            setDone(true);
        } catch (err) {
            toast.error("Could not submit the application. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <main data-testid="apply-page" className="text-white pt-32 pb-24">
            <SEO
                title="Apply for Ecosystem Access | Purposeful, Inc."
                description="Tell us what you're building, what you're looking for and where you need help. If there's a relevant fit within the Purposeful ecosystem, we'll explore the right introduction."
                path="/apply"
                keywords="apply ecosystem access, founder application, Purposeful ecosystem, incubator introductions, grant introductions, accelerator introductions"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "WebPage",
                    "url": "https://purposeful.inc/apply",
                    "name": "Apply for Ecosystem Access",
                    "isPartOf": { "@id": "https://purposeful.inc/#website" },
                }}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "Ecosystem", path: "/ecosystem" }, { name: "Apply", path: "/apply" }]}
            />

            <section className="container-x mx-auto max-w-4xl">
                <div className="overline mb-4">Ecosystem application</div>
                <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight">
                    Looking for the<br />
                    <span className="text-[#D4AF37]">right introduction?</span>
                </h1>
                <p className="mt-6 text-zinc-400 text-lg leading-relaxed max-w-2xl">
                    Tell us what you're building, what you're looking for and
                    where you need help. If there is a relevant fit within the
                    Purposeful ecosystem, we'll explore the right introduction.
                </p>
            </section>

            <section className="container-x mx-auto max-w-4xl mt-14">
                {done ? (
                    <div className="card-dark p-10 md:p-14 text-center" data-testid="apply-success">
                        <div className="overline mb-3">Received</div>
                        <h2 className="font-display text-3xl md:text-4xl">Thanks for applying.</h2>
                        <p className="mt-4 text-zinc-400 max-w-lg mx-auto">
                            We review every application personally. If there's
                            a genuine fit within the Purposeful ecosystem,
                            you'll hear from Chandrashekar directly.
                        </p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-10" data-testid="apply-form">
                        <Fieldset title="Your details" testId="fs-you">
                            <Field label="Name *"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apply-name" className={inputCls} /></Field>
                            <Field label="Email *"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="apply-email" className={inputCls} /></Field>
                            <Field label="LinkedIn"><input value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} data-testid="apply-linkedin" placeholder="linkedin.com/in/…" className={inputCls} /></Field>
                            <Field label="Phone (optional)"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="apply-phone" className={inputCls} /></Field>
                        </Fieldset>

                        <Fieldset title="Your business" testId="fs-biz">
                            <Field label="Company name *"><input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="apply-company" className={inputCls} /></Field>
                            <Field label="Website"><input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="apply-website" placeholder="https://…" className={inputCls} /></Field>
                            <Field label="Sector *"><input required value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} data-testid="apply-sector" placeholder="Climate / Health / Education / …" className={inputCls} /></Field>
                            <Field label="Geography *"><input required value={form.geography} onChange={(e) => setForm({ ...form, geography: e.target.value })} data-testid="apply-geography" placeholder="India / SEA / Global / …" className={inputCls} /></Field>
                            <Field label="Stage of business *" span2>
                                <select required value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="apply-stage" className={inputCls}>
                                    <option value="">Select a stage</option>
                                    {STAGES.map((s) => (<option key={s} value={s}>{s}</option>))}
                                </select>
                            </Field>
                        </Fieldset>

                        <Fieldset title="What are you looking for?" testId="fs-looking" hint="Select all that apply.">
                            <div className="col-span-2 flex flex-wrap gap-2">
                                {LOOKING_FOR.map((v) => {
                                    const active = form.looking_for.includes(v);
                                    return (
                                        <button
                                            type="button"
                                            key={v}
                                            onClick={() => toggleLooking(v)}
                                            data-testid={`apply-looking-${v.toLowerCase().replace(/\s/g, '-')}`}
                                            className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition ${
                                                active
                                                    ? "bg-[#D4AF37] text-white"
                                                    : "border border-white/15 text-zinc-300 hover:border-[#D4AF37]"
                                            }`}
                                        >
                                            {v}
                                        </button>
                                    );
                                })}
                            </div>
                        </Fieldset>

                        <Fieldset title="Tell us more" testId="fs-goal">
                            <Field label="What are you currently trying to achieve, and what kind of introduction would be most useful? *" span2>
                                <textarea required rows={6} value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="apply-goal" className={`${inputCls} resize-none`} />
                            </Field>
                        </Fieldset>

                        <Fieldset title="Additional information" testId="fs-additional" hint="Anything else that would help us assess your request.">
                            <Field label="Anything else you'd like us to know" span2>
                                <textarea rows={4} value={form.additional} onChange={(e) => setForm({ ...form, additional: e.target.value })} data-testid="apply-additional" className={`${inputCls} resize-none`} />
                            </Field>
                        </Fieldset>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
                            <button type="submit" disabled={loading} data-testid="apply-submit" className="btn-gold disabled:opacity-60">
                                {loading ? <Loader2 size={16} className="animate-spin" /> : <>Submit Application <Send size={14} /></>}
                            </button>
                            <p className="text-xs text-zinc-500 max-w-lg">
                                Purposeful does not guarantee funding, admission
                                or introductions. Applications are reviewed
                                based on relevance and fit within the ecosystem.
                            </p>
                        </div>
                    </form>
                )}
            </section>
        </main>
    );
}

const inputCls = "w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37] transition";

function Fieldset({ title, hint, children, testId }) {
    return (
        <div data-testid={testId}>
            <div className="mb-4">
                <h2 className="font-display text-xl md:text-2xl">{title}</h2>
                {hint && <p className="text-zinc-500 text-xs mt-1">{hint}</p>}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">{children}</div>
        </div>
    );
}

function Field({ label, children, span2 }) {
    return (
        <label className={`block ${span2 ? "sm:col-span-2" : ""}`}>
            <div className="text-xs uppercase tracking-widest font-mono text-zinc-500 mb-2">{label}</div>
            {children}
        </label>
    );
}
